/**
 * Where and how each audited thing was done (AIOE 0.67.0): the address it came from, an approximate
 * place, the user agent and, for a machine, the app and its version. Kept beside the audit row, not in
 * it: none of it is hashed (auditChain.ts), so it can be shortened or cleared when the organisation's
 * retention ends without breaking the chain. Rows the platform's own jobs write carry none.
 *
 *   GET /admin/v1/audit-settings   administrators: what is captured and for how long (AuditPrivacyView)
 *   PUT /admin/v1/audit-settings   administrators: AuditPrivacy; the change is itself audited (`audit.settings`)
 *
 * The place comes from an offline database on the server (DB-IP City Lite); an address never leaves
 * the service to be looked up. Private and mesh addresses are named in words instead.
 */
import { z } from "zod";
/** The header AIOE's own apps name themselves in, `<app>/<version> (<system>)`: the phone app sends `phone/0.1.0 (android)`. */
export const AIOE_CLIENT_HEADER = "x-aioe-client";
/** Where an address is, as far as the server can tell. `lookup`: from the offline database; the rest are named, not looked up. */
export const auditPlaceSourceSchema = z.enum(["lookup", "private", "mesh", "loopback", "unknown"]);
export const auditLocationSchema = z.object({
    /** ISO 3166-1 alpha-2. */
    country: z.string().max(2).optional(),
    /** The state or province. */
    region: z.string().max(120).optional(),
    city: z.string().max(120).optional(),
    source: auditPlaceSourceSchema,
});
/** What called: one of AIOE's machines or apps, a browser, a script. */
export const auditClientKindSchema = z.enum(["workbench", "node", "mesh-agent", "phone", "browser", "script", "other"]);
export const auditClientSchema = z.object({
    kind: auditClientKindSchema,
    /** The app or browser by name: "AI Workbench", "Chrome", "curl". */
    name: z.string().max(80).optional(),
    /** The app's version: a machine's from its fleet record, not only from the user agent. */
    version: z.string().max(40).optional(),
    /** The operating system in words: Windows, Mac, Linux, Android, iPhone. */
    os: z.string().max(40).optional(),
    /** All of it in words: "AI Workbench 0.89.19 on Windows", "Chrome 152 on Windows", "a script (curl)". */
    summary: z.string().max(160),
});
/** Where and how, as kept. Each part is absent when it was not captured, was turned off, or has been cleared. */
export const auditOriginSchema = z.object({
    /** The client address (through the trusted proxies), or its network when shortened ("203.0.113.0/24"). */
    ip: z.string().max(64).optional(),
    ipVersion: z.union([z.literal(4), z.literal(6)]).optional(),
    location: auditLocationSchema.optional(),
    userAgent: z.string().max(512).optional(),
    client: auditClientSchema.optional(),
});
/* ---------- the organisation's settings ---------- */
export const auditIpLevelSchema = z.enum(["full", "shortened", "off"]);
export const auditLocationLevelSchema = z.enum(["city", "region", "country", "off"]);
export const auditUserAgentLevelSchema = z.enum(["full", "summary", "off"]);
/** The longest these details may be kept, in days; 0 keeps them as long as the row. */
export const AUDIT_ORIGIN_KEEP_DAYS_MAX = 3650;
export const auditPrivacySchema = z.object({
    ip: auditIpLevelSchema,
    location: auditLocationLevelSchema,
    userAgent: auditUserAgentLevelSchema,
    /** Days the details are kept; then a daily job clears them and the row stays. 0: as long as the row. */
    keepDays: z.number().int().min(0).max(AUDIT_ORIGIN_KEEP_DAYS_MAX),
});
/** Enterprise keeps everything for 90 days; AI Cloud, a shared service, starts privacy-minded and the organisation may raise it. */
export const AUDIT_PRIVACY_DEFAULTS = {
    enterprise: { ip: "full", location: "city", userAgent: "full", keepDays: 90 },
    cloud: { ip: "shortened", location: "region", userAgent: "summary", keepDays: 30 },
};
export const AUDIT_IP_WORDS = { full: "The whole address", shortened: "Shortened to its network", off: "Not kept" };
export const AUDIT_LOCATION_WORDS = { city: "Town or city", region: "State or region", country: "Country only", off: "Not kept" };
export const AUDIT_USER_AGENT_WORDS = { full: "In full", summary: "A summary only", off: "Not kept" };
/* ---------- in words ---------- */
/** "Sydney, AU"; "New South Wales, AU"; "AU"; "Private network"; "On the mesh". Undefined when nothing is known. */
export function auditPlaceWords(l) {
    if (!l)
        return undefined;
    if (l.source === "private")
        return "Private network";
    if (l.source === "mesh")
        return "On the mesh";
    if (l.source === "loopback")
        return "This server";
    const local = l.city ?? l.region;
    if (local && l.country)
        return `${local}, ${l.country}`;
    return local ?? l.country;
}
/** "from Sydney, AU · Chrome 152 on Windows": the quiet line under an audit row. Undefined when nothing was kept. */
export function auditOriginWords(o) {
    if (!o)
        return undefined;
    const place = auditPlaceWords(o.location);
    const parts = [place ? `from ${place}` : o.ip ? `from ${o.ip}` : undefined, o.client?.summary].filter((p) => Boolean(p));
    return parts.length ? parts.join(" · ") : undefined;
}
//# sourceMappingURL=auditOrigin.js.map